/**
 * ViewUserPermissionsModal Component
 *
 * Displays all permissions a user has (from role + overrides)
 * Allows granting temporary permissions and revoking overrides
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Shield,
  Search,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Trash2
} from 'lucide-react';
import {
  superAdminApi,
  AdminUser,
  UserPermissionsResponse,
  UserEffectivePermission,
  UserPermissionOverride,
  RevokePermissionData
} from '../../services/adminApi';
import { RevokePermissionConfirmModal } from './RevokePermissionConfirmModal';

interface ViewUserPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminUser | null;
  onGrantPermission?: (userId: number) => void;
  onRevokeOverride?: (userId: number, permissionKey: string) => Promise<void>;
  onRefresh?: () => void;
}

interface GroupedPermission {
  key: string;
  category: string;
  hasPermission: boolean;
  source: 'from_role' | 'granted' | 'revoked' | 'none';
  expiresAt: string | null;
  override?: UserPermissionOverride;
}

const CATEGORY_ICONS: Record<string, string> = {
  admin: '👤',
  analytics: '📊',
  api: '🔌',
  carriers: '🚗',
  crashes: '⚠️',
  drivers: '👷',
  fleet: '🚛',
  freight: '📦',
  inspections: '🔍',
  insurance: '🛡️',
  monitoring: '📡',
  shippers: '🏢',
  vins: '#️⃣',
  violations: '⛔',
};

export const ViewUserPermissionsModal: React.FC<ViewUserPermissionsModalProps> = ({
  isOpen,
  onClose,
  user,
  onGrantPermission,
  onRevokeOverride,
  onRefresh,
}) => {
  const [permissionsData, setPermissionsData] = useState<UserPermissionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [removingOverride, setRemovingOverride] = useState<string | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokePermission, setRevokePermission] = useState<GroupedPermission | null>(null);

  // Fetch permissions when modal opens
  useEffect(() => {
    if (isOpen && user) {
      fetchPermissions();
    }
  }, [isOpen, user]);

  const fetchPermissions = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const data = await superAdminApi.getUserPermissions(user.id);
      setPermissionsData(data);
      // Expand all categories by default
      if (data.permissions.length > 0) {
        const categories = new Set(data.permissions.map(p => p.permission_category));
        setExpandedCategories(categories);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  // Group permissions by category
  const groupedPermissions = useMemo(() => {
    if (!permissionsData) return {};

    const groups: Record<string, GroupedPermission[]> = {};

    // Add all effective permissions
    permissionsData.permissions.forEach((perm) => {
      if (!groups[perm.permission_category]) {
        groups[perm.permission_category] = [];
      }

      groups[perm.permission_category].push({
        key: perm.permission_key,
        category: perm.permission_category,
        hasPermission: true,
        source: perm.source,
        expiresAt: perm.expires_at,
      });
    });

    // Add revoked overrides (permissions user doesn't have but are explicitly revoked)
    permissionsData.overrides
      .filter((override) => !override.granted)
      .forEach((override) => {
        if (!groups[override.category]) {
          groups[override.category] = [];
        }

        // Check if permission already exists in group
        const existingIndex = groups[override.category].findIndex(
          p => p.key === override.permission_key
        );

        if (existingIndex === -1) {
          groups[override.category].push({
            key: override.permission_key,
            category: override.category,
            hasPermission: false,
            source: 'revoked',
            expiresAt: null,
            override,
          });
        }
      });

    // Add override details to granted permissions
    permissionsData.overrides
      .filter((override) => override.granted)
      .forEach((override) => {
        if (groups[override.category]) {
          const perm = groups[override.category].find(
            p => p.key === override.permission_key
          );
          if (perm) {
            perm.override = override;
          }
        }
      });

    // Sort permissions within each category
    Object.keys(groups).forEach((category) => {
      groups[category].sort((a, b) => a.key.localeCompare(b.key));
    });

    return groups;
  }, [permissionsData]);

  // Filter permissions by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groupedPermissions;

    const filtered: Record<string, GroupedPermission[]> = {};
    const query = searchQuery.toLowerCase();

    Object.entries(groupedPermissions).forEach(([category, permissions]) => {
      const matchingPerms = permissions.filter(
        (perm) =>
          perm.key.toLowerCase().includes(query) ||
          category.toLowerCase().includes(query)
      );

      if (matchingPerms.length > 0) {
        filtered[category] = matchingPerms;
      }
    });

    return filtered;
  }, [groupedPermissions, searchQuery]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const handleRemoveOverride = (permission: GroupedPermission) => {
    setRevokePermission(permission);
    setShowRevokeModal(true);
  };

  const handleConfirmRevoke = async (reason: string) => {
    if (!user || !revokePermission) return;

    setRemovingOverride(revokePermission.key);
    try {
      // Call revoke API with reason
      const revokeData: RevokePermissionData = {
        permission_key: revokePermission.key,
        reason: reason,
      };
      await superAdminApi.revokePermission(user.id, revokeData);

      // Refresh permissions
      await fetchPermissions();
      if (onRefresh) onRefresh();

      // Close modal
      setShowRevokeModal(false);
      setRevokePermission(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke permission');
      throw err;
    } finally {
      setRemovingOverride(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysUntil = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntil <= 7 && daysUntil > 0;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl transform rounded-2xl bg-white shadow-xl transition-all max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                User Permissions - {user?.email}
              </h3>
              {permissionsData && (
                <p className="text-sm text-gray-600 mt-1">
                  Role: {permissionsData.role?.display_name || 'No Role'} ({permissionsData.summary.from_role} base permissions)
                  {permissionsData.summary.granted > 0 && (
                    <span className="text-green-600"> +{permissionsData.summary.granted} granted</span>
                  )}
                  {permissionsData.summary.revoked > 0 && (
                    <span className="text-red-600"> -{permissionsData.summary.revoked} revoked</span>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search and Actions */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search permissions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {onGrantPermission && (
              <button
                onClick={() => user && onGrantPermission(user.id)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Grant Permission
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && permissionsData && (
              <div className="space-y-4">
                {Object.entries(filteredGroups).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No permissions found matching "{searchQuery}"
                  </div>
                ) : (
                  Object.entries(filteredGroups).map(([category, permissions]) => {
                    const isExpanded = expandedCategories.has(category);
                    const icon = CATEGORY_ICONS[category.toLowerCase()] || '📋';

                    return (
                      <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Category Header */}
                        <button
                          onClick={() => toggleCategory(category)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{icon}</span>
                            <span className="font-medium text-gray-900 capitalize">
                              {category}
                            </span>
                            <span className="text-sm text-gray-500">
                              ({permissions.filter(p => p.hasPermission).length}/{permissions.length} permissions)
                            </span>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>

                        {/* Category Permissions */}
                        {isExpanded && (
                          <div className="divide-y divide-gray-100">
                            {permissions.map((perm) => (
                              <div
                                key={perm.key}
                                className="px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  {perm.hasPermission ? (
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {perm.key}
                                    </p>
                                    {perm.override && (
                                      <p className="text-xs text-gray-500">
                                        {perm.override.granted ? 'Granted' : 'Revoked'} by{' '}
                                        {perm.override.granted_by_email} •{' '}
                                        {perm.override.reason || 'No reason provided'}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  {/* Source Badge */}
                                  {perm.source === 'from_role' && (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                                      From Role
                                    </span>
                                  )}
                                  {perm.source === 'granted' && (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                                      <span>⭐</span>
                                      GRANTED
                                    </span>
                                  )}
                                  {perm.source === 'revoked' && (
                                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                                      REVOKED
                                    </span>
                                  )}

                                  {/* Expiration */}
                                  {perm.expiresAt && (
                                    <span
                                      className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${
                                        isExpiringSoon(perm.expiresAt)
                                          ? 'bg-yellow-100 text-yellow-700'
                                          : 'bg-blue-100 text-blue-700'
                                      }`}
                                    >
                                      <Clock className="w-3 h-3" />
                                      Exp: {formatDate(perm.expiresAt)}
                                    </span>
                                  )}

                                  {/* Remove Override Button */}
                                  {perm.override && (
                                    <button
                                      onClick={() => handleRemoveOverride(perm)}
                                      disabled={removingOverride === perm.key}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                      title="Revoke Permission"
                                    >
                                      {removingOverride === perm.key ? (
                                        <div className="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin" />
                                      ) : (
                                        <Trash2 className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {permissionsData && (
                  <>
                    <span className="font-medium">{permissionsData.summary.total_permissions}</span> total permissions
                    {' • '}
                    <span className="font-medium">{Object.keys(groupedPermissions).length}</span> categories
                  </>
                )}
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Revoke Permission Confirmation Modal */}
      <RevokePermissionConfirmModal
        isOpen={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false);
          setRevokePermission(null);
        }}
        onConfirm={handleConfirmRevoke}
        user={user}
        permissionKey={revokePermission?.key || ''}
        permissionCategory={revokePermission?.category}
        permissionDescription={revokePermission?.override?.reason || undefined}
      />
    </div>
  );
};
